const REVIEW_STATUS = {
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED',
  PENDING: 'PENDING',
  SUBMITTED: 'SUBMITTED'
};

/**
 * Maps backend section_key values to merchant onboarding step numbers.
 */
const CORPORATE_STEP_MAP = {
  BUSINESS_DETAILS: 1,
  OWNERSHIP_DIRECTORS: 2,
  OWNERSHIP_SHAREHOLDERS: 2,
  OWNERSHIP_UBOS: 2,
  OWNERSHIP_DOCUMENTS: 2,
  PEP_DECLARATION: 3,
  COMPLIANCE_DOCS: 4,
  DUE_DILIGENCE: 5
};

const SECTION_KEY_TO_STEP = {
  FREELANCER: {
    FREELANCER_BASIC_DETAILS: 1,
    FREELANCER_ADDITIONAL_INFO: 2
  },
  CORP_NON_FI: CORPORATE_STEP_MAP,
  CORP_FI: CORPORATE_STEP_MAP
};

const STEP_STATUS_PRIORITY = [
  REVIEW_STATUS.DECLINED,
  REVIEW_STATUS.PENDING,
  REVIEW_STATUS.SUBMITTED,
  REVIEW_STATUS.APPROVED
];

const normalizeSectionStatus = (status) => {
  if (typeof status !== 'string') return null;
  const normalized = status.trim().toUpperCase();
  if (!STEP_STATUS_PRIORITY.includes(normalized)) return null;
  return normalized;
};

const deriveStatusFromFieldReviews = (entries = []) => {
  const reviews = entries.flatMap((entry) => entry.field_reviews || []);
  if (reviews.length === 0) return null;

  if (reviews.some((review) => review.status === REVIEW_STATUS.DECLINED)) {
    return REVIEW_STATUS.DECLINED;
  }

  if (reviews.every((review) => review.status === REVIEW_STATUS.APPROVED)) {
    return REVIEW_STATUS.APPROVED;
  }

  return null;
};

/**
 * Derives per-step statuses from backend section lifecycle statuses.
 * Falls back to field_reviews only when section status is unavailable.
 *
 * @param {object} profile - Compliance profile from the API
 * @returns {{ [stepNumber: number]: { status: string, reasons: string[] } }}
 */
const deriveStepReviewStatuses = (profile) => {
  if (!profile?.sections) return {};

  const merchantType = profile.merchant_type;
  const mapping = SECTION_KEY_TO_STEP[merchantType];
  if (!mapping) return {};

  // Collect section statuses and decline reasons grouped by step.
  const groupedByStep = {};
  const getStepGroup = (stepNumber) => {
    if (!groupedByStep[stepNumber]) {
      groupedByStep[stepNumber] = { statuses: [], reasons: [] };
    }
    return groupedByStep[stepNumber];
  };

  for (const section of profile.sections) {
    const stepNumber = mapping[section.section_key];
    if (!stepNumber) continue;

    const sectionEntries = section.entries || [];
    const stepGroup = getStepGroup(stepNumber);
    const sectionStatus =
      normalizeSectionStatus(section.status) || deriveStatusFromFieldReviews(sectionEntries);

    if (sectionStatus) {
      stepGroup.statuses.push(sectionStatus);
    }

    for (const entry of sectionEntries) {
      for (const review of entry.field_reviews || []) {
        if (review.status === REVIEW_STATUS.DECLINED && review.reason) {
          stepGroup.reasons.push(review.reason);
        }
      }
    }
  }

  // Resolve one status per step using priority.
  const result = {};
  for (const [step, { statuses, reasons }] of Object.entries(groupedByStep)) {
    const resolvedStatus = STEP_STATUS_PRIORITY.find((status) => statuses.includes(status));
    if (!resolvedStatus) continue;

    const dedupedReasons = [...new Set(reasons)];

    result[Number(step)] = {
      status: resolvedStatus,
      reasons: dedupedReasons
    };
  }

  return result;
};

/**
 * Builds store state for re-submission mode when a profile has been declined.
 *
 * @param {object} params
 * @param {number[]} params.allStepIds — all step IDs in the flow (e.g. [1, 2])
 * @param {object} params.reviewStatuses — result from deriveStepReviewStatuses
 * @param {object} params.mappedStepData — step data mapped from the profile
 * @returns {object} state to pass to store.setState
 */
const buildDeclinedStoreState = ({ allStepIds, reviewStatuses, mappedStepData }) => {
  const firstDeclinedStep = allStepIds.find(
    (id) => reviewStatuses[id]?.status === REVIEW_STATUS.DECLINED
  );
  return {
    stepData: mappedStepData,
    completedStepIds: allStepIds,
    currentStep: firstDeclinedStep || 1,
    stepReviewStatuses: reviewStatuses,
    showSuccess: false
  };
};

/**
 * Returns true when a step should be read-only during a declined resubmission.
 * Only DECLINED steps are editable; all other statuses are locked.
 * Returns false when there are no review statuses (i.e. initial submission — everything editable).
 *
 * @param {object} stepReviewStatuses — result from deriveStepReviewStatuses stored in the flow
 * @param {number} step — the step number to check
 * @returns {boolean}
 */
const isStepReadOnly = (stepReviewStatuses, step) => {
  if (!stepReviewStatuses || Object.keys(stepReviewStatuses).length === 0) return false;
  return stepReviewStatuses[step]?.status !== REVIEW_STATUS.DECLINED;
};

export {
  buildDeclinedStoreState,
  deriveStepReviewStatuses,
  isStepReadOnly,
  REVIEW_STATUS,
  SECTION_KEY_TO_STEP
};
